import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTenancy } from '@/contexts/TenancyContext';
import { checklistService } from '../services/checklist-service';
import type { ChecklistTemplate, Checklist } from '../types/checklist.types';
import { Finding } from '../../auditcentre';

export function useChecklist() {
  const { user } = useAuth();
  const { activeTenancy } = useTenancy();
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<ChecklistTemplate | null>(null);
  const [currentChecklist, setCurrentChecklist] = useState<Checklist | null>(null);

  // Load all templates
  const loadTemplates = useCallback(async () => {
    if (!activeTenancy?.id) return;
    
    try {
      setLoading(true);
      const data = await checklistService.getTemplates(activeTenancy.id);
      setTemplates(data);
    } catch (err) {
      console.error('Failed to load templates:', err);
      setError(err instanceof Error ? err : new Error('Failed to load templates'));
    } finally {
      setLoading(false);
    }
  }, [activeTenancy?.id]);

  // Load user's checklists
  const loadChecklists = useCallback(async () => {
    if (!activeTenancy?.id || !user?.uid) return;
    
    try {
      setLoading(true);
      const data = await checklistService.getChecklists(activeTenancy.id);
      setChecklists(data);
    } catch (err) {
      console.error('Failed to load checklists:', err);
      setError(err instanceof Error ? err : new Error('Failed to load checklists'));
    } finally {
      setLoading(false);
    }
  }, [activeTenancy?.id, user?.uid]);

  // Load initial data
  useEffect(() => {
    loadTemplates();
    loadChecklists();
  }, [loadTemplates, loadChecklists]);

  // Create a new template
  const createTemplate = async (templateData: Omit<ChecklistTemplate, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!activeTenancy?.id || !user?.uid) {
      throw new Error('User not authenticated or tenant not selected');
    }

    try {
      setLoading(true);
      const newTemplate = await checklistService.saveTemplate({
        ...templateData,
        tenantId: activeTenancy.id,
        createdBy: user.uid,
      });
      
      await loadTemplates();
      return newTemplate;
    } catch (err) {
      console.error('Failed to create template:', err);
      throw err instanceof Error ? err : new Error('Failed to create template');
    } finally {
      setLoading(false);
    }
  };

  // Update an existing template
  const updateTemplate = async (id: string, updates: Partial<ChecklistTemplate>) => {
    try {
      setLoading(true);
      const updatedTemplate = await checklistService.saveTemplate({
        id,
        ...updates,
        updatedAt: new Date(),
        tenantId: activeTenancy.id,
      });
      
      await loadTemplates();
      if (selectedTemplate?.id === id) {
        setSelectedTemplate(updatedTemplate);
      }
      return updatedTemplate;
    } catch (err) {
      console.error('Failed to update template:', err);
      throw err instanceof Error ? err : new Error('Failed to update template');
    } finally {
      setLoading(false);
    }
  };

  // Delete a template
  const deleteTemplate = async (id: string) => {
    try {
      setLoading(true);
      await checklistService.deleteTemplate(id, activeTenancy.id);
      
      await loadTemplates();
      if (selectedTemplate?.id === id) {
        setSelectedTemplate(null);
      }
    } catch (err) {
      console.error('Failed to delete template:', err);
      throw err instanceof Error ? err : new Error('Failed to delete template');
    } finally {
      setLoading(false);
    }
  };

  // Get a single checklist by ID
  const getChecklist = async (id: string) => {
    if (!activeTenancy?.id) {
      throw new Error('No active tenancy');
    }

    try {
      setLoading(true);
      const checklist = await checklistService.getChecklist(id, activeTenancy.id);
      if (checklist) {
        setCurrentChecklist(checklist);
      }
      return checklist;
    } catch (err) {
      console.error('Failed to get checklist:', err);
      setError(err instanceof Error ? err : new Error('Failed to get checklist'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Create a new checklist from a template
  const createChecklist = async (templateId: string) => {
    if (!user?.uid || !activeTenancy?.id) {
      throw new Error('User not authenticated or no active tenancy');
    }

    try {
      setLoading(true);
      const newChecklist = await checklistService.createChecklistFromTemplate(
        templateId,
        activeTenancy.id,
        user.uid
      );
      
      await loadChecklists();
      setCurrentChecklist(newChecklist);
      return newChecklist;
    } catch (err) {
      console.error('Failed to create checklist:', err);
      setError(err instanceof Error ? err : new Error('Failed to create checklist'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update a checklist
  const updateChecklist = async (id: string, updates: Partial<Checklist>) => {
    if (!user?.uid || !activeTenancy?.id) {
      throw new Error('User not authenticated or no active tenancy');
    }

    try {
      setLoading(true);
      await checklistService.updateChecklist(id, updates, activeTenancy.id);
      
      setChecklists(prev => 
        prev.map(c => 
          c.id === id 
            ? { ...c, ...updates, updatedAt: new Date() } 
            : c
        )
      );
      
      if (currentChecklist?.id === id) {
        setCurrentChecklist(prev => prev ? { ...prev, ...updates, updatedAt: new Date() } : null);
      }
    } catch (err) {
      console.error('Failed to update checklist:', err);
      setError(err instanceof Error ? err : new Error('Failed to update checklist'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Submit a checklist (mark as completed)
  const submitChecklist = async (id: string, values: Record<string, any>, signature?: string, items?: {id: string, title: string}[], findings?: Finding[]) => {
    if (!user?.uid || !activeTenancy?.id) {
      throw new Error('User not authenticated or no active tenancy');
    }

    try {
      setLoading(true);
      // Transform values: {itemId: value} => {itemId: {title, value}}
      let valuesWithTitles: Record<string, { title: string, value: any }> = {};
      if (Array.isArray(items)) {
        for (const item of items) {
          if (values.hasOwnProperty(item.id)) {
            valuesWithTitles[item.id] = { title: item.title, value: values[item.id] };
          }
        }
      } else {
        // fallback: store as before
        valuesWithTitles = values as any;
      }
      await checklistService.submitChecklist(id, user.uid, activeTenancy.id, signature, valuesWithTitles, findings);
      // Ensure we reload from Firestore so other views (like history) see fresh data
      await loadChecklists();
      
      setChecklists(prev => 
        prev.map(c => 
          c.id === id 
            ? { 
                ...c, 
                status: 'completed',
                updatedAt: new Date(),
                completionHistory: [
                  ...(c.completionHistory || []),
                  {
                    completedAt: new Date(),
                    completedBy: user.uid,
                    values,
                    ...(signature ? { signature } : {})
                  }
                ]
              } 
            : c
        )
      );
      
      if (currentChecklist?.id === id) {
        // Optionally refresh the currentChecklist from Firestore for complete accuracy
        try {
          const latest = await checklistService.getChecklist(id, activeTenancy.id);
          if (latest) setCurrentChecklist(latest);
        } catch {
          // fall back to local optimistic update if fetch fails
          setCurrentChecklist(prev => 
            prev 
              ? { 
                  ...prev, 
                  status: 'completed',
                  updatedAt: new Date(),
                  completionHistory: [
                    ...(prev.completionHistory || []),
                    {
                      completedAt: new Date(),
                      completedBy: user.uid,
                      values,
                      ...(signature ? { signature } : {})
                    }
                  ]
                } 
              : null
          );
        }
      }
    } catch (err) {
      console.error('Failed to submit checklist:', err);
      setError(err instanceof Error ? err : new Error('Failed to submit checklist'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Public method to refresh checklists from Firestore
  const refreshChecklists = async () => {
    await loadChecklists();
  };

  return {
    // State
    templates,
    checklists,
    loading,
    error,
    selectedTemplate,
    currentChecklist,
    
    // Actions
    setSelectedTemplate,
    setCurrentChecklist,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    createChecklist,
    updateChecklist,
    submitChecklist,
    refreshTemplates: loadTemplates,
    refreshChecklists: loadChecklists,
  };
}
